import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { GoogleOAuthProvider, useGoogleLogin } from "@react-oauth/google";
import { gapi } from "gapi-script";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { setLogoutInProgress, clearInvestorLocalStorage } from "@/utils/investors/databaseUtils";

// Define the Google user type
interface GoogleUser {
  access_token: string;
  email: string;
  name: string;
  picture: string;
}

// Define context type
interface GoogleAuthContextType {
  user: GoogleUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  supabaseSession: any | null;
  authError: string | null; // Added authError property to the context type
}

// Create context with default values
const GoogleAuthContext = createContext<GoogleAuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: () => {},
  logout: () => {},
  supabaseSession: null,
  authError: null // Added default value
});

// Client ID - replaced with the provided Google Client ID
const GOOGLE_CLIENT_ID = "1059806949588-2j13nduc15vqthgckdpfdcg82r54leh0.apps.googleusercontent.com";

// Scopes for Google Sheets API
const SCOPES = [
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/userinfo.profile"
];

export const GoogleAuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<GoogleUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [supabaseSession, setSupabaseSession] = useState<any>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  // Flag to prevent data loading after logout
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [isLogoutInProgress, setLogoutInProgress] = useState(false);

  // Set up Supabase auth state listener
  useEffect(() => {
    const setupSupabaseAuth = async () => {
      // Check if user is already authenticated in Supabase
      const { data: sessionData } = await supabase.auth.getSession();
      
      // Only set session if we're not in a logged-out state
      if (!isLoggedOut) {
        setSupabaseSession(sessionData.session);
      }

      // Set up auth state change listener
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (event, session) => {
          if (event === 'SIGNED_OUT') {
            // User has signed out, clear all state
            setSupabaseSession(null);
            setUser(null);
            setIsLoggedOut(true);
          } else if (event === 'SIGNED_IN' && !isLoggedOut) {
            // Only update state if not in logged out state
            setSupabaseSession(session);
            setAuthError(null); // Clear any previous errors
            setIsLoggedOut(false);
          }
        }
      );

      return () => {
        subscription.unsubscribe();
      };
    };

    setupSupabaseAuth();
  }, [isLoggedOut]);

  useEffect(() => {
    // Initialize the Google API client
    const initGoogleApi = async () => {
      try {
        await new Promise((resolve) => {
          gapi.load("client:auth2", resolve);
        });
        
        await gapi.client.init({
          clientId: GOOGLE_CLIENT_ID,
          scope: SCOPES.join(" "),
        });
        
        // Only check for saved user if not logged out
        if (!isLoggedOut) {
          // Check if user is already logged in
          const savedUser = localStorage.getItem("google_user");
          if (savedUser) {
            const parsedUser = JSON.parse(savedUser);
            setUser(parsedUser);
            
            // Sign in to Supabase if we have Google user but no Supabase session
            if (parsedUser && !supabaseSession) {
              signInToSupabase(parsedUser.email);
            }
          }
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error initializing Google API:", error);
        setIsLoading(false);
      }
    };

    initGoogleApi();
  }, [supabaseSession, isLoggedOut]);

  // Function to sign in to Supabase with email and password
  const signInToSupabase = async (email: string) => {
    try {
      // Use the email as password for simplicity in this demo
      // In production, you would want a more secure approach
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password: `google-auth-${email}` // This is a simplification - in production use a secure method
      });

      if (error) {
        // If error contains "Email not confirmed", try to auto-confirm for development 
        if (error.message.includes("Email not confirmed")) {
          console.log("Supabase login error: Email not confirmed");
          toast.error("Email verification required. For development, you can disable email confirmation in the Supabase dashboard.");
          setAuthError("email_not_confirmed");
          
          // Try to sign up again as fallback (this will create a new account if it doesn't exist)
          await signUpWithSupabase(email);
        } else if (error.message.includes("Invalid login credentials")) {
          // If user doesn't exist, create a new account
          await signUpWithSupabase(email);
        } else {
          console.error("Supabase login error:", error.message);
          toast.error(`Authentication error: ${error.message}`);
          setAuthError(error.message);
        }
      } else {
        toast.success("Authenticated with Supabase");
        setAuthError(null);
      }
    } catch (err) {
      console.error("Error signing in to Supabase:", err);
      toast.error("Failed to authenticate with Supabase");
    }
  };

  // Function to sign up with Supabase
  const signUpWithSupabase = async (email: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password: `google-auth-${email}`, // This is a simplification - in production use a secure method
      });

      if (error) {
        console.error("Supabase signup error:", error.message);
        toast.error(`Account creation error: ${error.message}`);
      } else {
        if (data.user && data.user.identities && data.user.identities.length === 0) {
          // User exists but has no identities (typically means user exists but is not confirmed)
          toast.warning("Your email needs verification. Check your inbox or disable email confirmation in Supabase dashboard.");
        } else {
          toast.success("Account created in Supabase. Please check your email for verification link.");
        }
      }
    } catch (err) {
      console.error("Error signing up with Supabase:", err);
      toast.error("Failed to create account in Supabase");
    }
  };

  // Set up Google login
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Get user info using access token
        const userInfoResponse = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        });
        
        const userInfo = await userInfoResponse.json();
        
        const googleUser = {
          access_token: tokenResponse.access_token,
          email: userInfo.email,
          name: userInfo.name,
          picture: userInfo.picture
        };
        
        // Save user to state and localStorage
        setUser(googleUser);
        localStorage.setItem("google_user", JSON.stringify(googleUser));
        
        // Also sign in to Supabase
        await signInToSupabase(userInfo.email);
      } catch (error) {
        console.error("Error fetching user info:", error);
        toast.error("Failed to fetch user information");
      }
    },
    onError: (error) => {
      console.error("Login Failed:", error);
      toast.error("Google login failed");
    },
    scope: SCOPES.join(" ")
  });

  const login = () => {
    setAuthError(null); // Clear any previous errors
    setIsLoggedOut(false); // Reset logout state
    googleLogin();
  };

  const logout = async () => {
    // Set logout flag first to prevent any data operations
    setLogoutInProgress(true);
    
    // Set logged out flag to prevent any data reloading
    setIsLoggedOut(true);
    
    // Clear UI state
    setUser(null);
    setSupabaseSession(null);
    
    // Only remove google_user from localStorage - keep API keys intact
    localStorage.removeItem("google_user");
    
    // Clear only the investor-related localStorage items
    clearInvestorLocalStorage();
    
    // Double check by explicitly removing these items if they exist
    localStorage.removeItem("investors_data");
    localStorage.removeItem("contacts_data");
    localStorage.removeItem("processed_investor_ids");
    
    // Attempt to revoke Google token
    try {
      if (gapi.auth2) {
        const authInstance = gapi.auth2.getAuthInstance();
        if (authInstance) {
          await authInstance.signOut();
          console.log("Google sign out successful");
        }
      }
    } catch (error) {
      console.error("Error signing out from Google:", error);
    }
    
    // Sign out from Supabase
    try {
      await supabase.auth.signOut();
      toast.info("Signed out from all services");
    } catch (error) {
      console.error("Error signing out from Supabase:", error);
    }
    
    // Reset logout flag after sign out is complete
    setLogoutInProgress(false);
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    supabaseSession,
    authError // Include authError in the context value
  };

  return (
    <GoogleAuthContext.Provider value={value}>
      {children}
    </GoogleAuthContext.Provider>
  );
};

export const useGoogleAuth = () => useContext(GoogleAuthContext);

// Wrap your application with this provider
export const GoogleAuthWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <GoogleAuthProvider>{children}</GoogleAuthProvider>
    </GoogleOAuthProvider>
  );
};
